// Defaults

interface DefaultOptionsI {
  format: string;
  quality: number;
  width: number;
  height: number;
  crossOrigin: string;
  large: boolean;
}

const defaultOptions: DefaultOptionsI = {
  format: "image/png",
  quality: 0.92,
  width: 1200,
  height: 1200,
  crossOrigin: "Anonymous",
  large: false,
};

export class ImageMergeError extends Error {}

// Return Promise
export const mergeImages = (
  sources: any[],
  opt?: DefaultOptionsI
): Promise<string> =>
  new Promise((resolve) => {
    const options = Object.assign({} as DefaultOptionsI, defaultOptions, opt);

    const canvas = document.createElement("canvas") as HTMLCanvasElement;

    // Load sources
    const images = sources.map(
      (source) =>
        new Promise((resolve, reject) => {
          // Convert sources to objects
          if (source.constructor.name !== "Object") {
            source = {
              src: source.src,
              cancasStyle: source.canvasStyle,
              scaledAssets: source.scaledAssets,
              verticalImages: source.verticalImages,
            };
          }

          // Resolve source and img when loaded
          const img = new Image();
          img.crossOrigin = options.crossOrigin;
          img.onerror = () =>
            reject(new ImageMergeError("Couldn't load image"));
          img.onload = () => resolve(Object.assign({}, source, { img }));
          img.src = source.src;
        })
    );

    // Get canvas context
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

    // When sources have loaded
    resolve(
      Promise.all(images).then((images: any) => {
        // Set canvas dimensions
        const getSize = (dim: "width" | "height") =>
          options[dim] ||
          Math.max(...images.map((image: any) => image.img[dim]));
        canvas.width = getSize("width");
        canvas.height = getSize("height");

        // Draw images to canvas
        images.forEach((image: any) => {
          ctx.globalAlpha = image.opacity ? image.opacity : 1;

          ctx.drawImage(
            image.img,
            image.x || 0,
            image.y || 0,
            canvas.width,
            canvas.height
          );
          ctx.restore();
        });
        // Resolve all other data URIs sync
        return canvas.toDataURL(options.format, options.quality);
      })
    );
  });
